import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
  inject
} from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { saveAs } from 'file-saver';
import { of } from 'rxjs';
import { ProposalRequestFileForm } from 'src/app/classes/form-groups/proposal-request-file-form';
import { ProposalRequestForm } from 'src/app/classes/form-groups/proposal-request-form';
import { ProposalRequestLineItemForm } from 'src/app/classes/form-groups/proposal-request-line-item.form';
import { ProposalRequestVendorForm } from 'src/app/classes/form-groups/proposal-request-vendor-form';
import { QuickSourceProgressForm } from 'src/app/classes/form-groups/quick-source-progress-form';
import {
  Company,
  ProposalRequest,
  ProposalRequestFile,
  Site,
  User
} from 'src/app/classes/models';
import { QuickSourceProgress } from 'src/app/classes/models/quick-source-progress';
import { QuickSourceProgressName } from 'src/app/interfaces/quick-source-progress-name';
import { AuthService } from 'src/app/services/auth.service';
import { CompanyService } from 'src/app/services/company.service';
import { ProposalRequestService } from 'src/app/services/proposal-request.service';
import { SharedDataService } from 'src/app/services/shared-data.service';
import { ToastService } from 'src/app/services/toast.service';
import { UtilityService } from 'src/app/services/utility.service';
import { proposalRequestDatesValidator } from 'src/app/validators/proposal-request-dates-validator';
import { proposalRequestInstructionFilesValidator } from 'src/app/validators/proposal-request-instructions-files-validator';

declare type StepRoutes =
  | 'site-selection'
  | 'location-contacts'
  | 'date-pricing-instructions'
  | 'add-vendors'
  | 'review';

@Component({
  selector: 'app-quicksource-creation',
  templateUrl: './quicksource-creation.component.html',
  styleUrls: ['./quicksource-creation.component.less'],
  providers: [SharedDataService],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class QuicksourceCreationComponent implements OnInit {
  private _cdr = inject(ChangeDetectorRef);

  constructor(
    private ToastService: ToastService,
    private SharedDataService: SharedDataService,
    private ProposalRequestService: ProposalRequestService,
    private route: ActivatedRoute,
    private UtilityService: UtilityService,
    private router: Router,
    private CompanyService: CompanyService,
    private AuthService: AuthService
  ) {
    this.LoggedInUser = new User(this.AuthService.getUser());

    this.ProposalRequestFormGroup.addValidators([
      proposalRequestInstructionFilesValidator,
      proposalRequestDatesValidator
    ]);

    this.SharedDataService.listenForEvent<
      Site | ProposalRequestForm | ProposalRequestFile | null
    >().subscribe({
      next: ({ eventName, data }) => {
        if (eventName === 'GetProposalRequestForm') {
          this.SharedDataService.sendEvent(
            'SetProposalRequestForm',
            this.ProposalRequestFormGroup
          );
        }

        if (eventName === 'GetProposalRequest') {
          this.SharedDataService.sendEvent(
            'SetProposalRequest',
            new ProposalRequest(this.ProposalRequestFormGroup.value)
          );
        }

        if (
          eventName === 'SiteSelected' &&
          (this.isSite(data) || data === undefined)
        ) {
          this.ProposalRequestSite = data;
        }

        if (eventName === 'GetSite' && this.ProposalRequestSite) {
          this.SharedDataService.sendEvent(
            'SetSite',
            new Site(this.ProposalRequestSite)
          );
        }

        if (eventName === 'GetCompany') {
          this.SharedDataService.sendEvent('SetCompany', this.Company);
        }

        if (
          eventName === 'DownloadProposalRequestFile' &&
          data instanceof ProposalRequestFile
        ) {
          this.downloadProposalRequestFile(data);
        }

        if (eventName === 'GetDisableInput') {
          this.SharedDataService.sendEvent(
            'SetDisableInput',
            this.disableInput
          );
        }

        if (eventName === 'GetLoggedInUser') {
          this.SharedDataService.sendEvent(
            'SetLoggedInUser',
            this.LoggedInUser
          );
        }
      }
    });
  }

  // If the url action is 'view', disable input on all forms.
  disableInput: boolean = false;
  Company!: Company;
  LoggedInUser: User;

  // May need to set this. Angular may not update changes to the map.
  QuickSourceProgressMap = new Map<
    QuickSourceProgressName,
    QuickSourceProgress | null
  >([
    ['nameSite', null],
    ['locationContact', null],
    ['datePricingInstructions', null],
    ['vendorSelection', null],
    ['review', null]
  ]);

  QuickSourceProgressNameToRouteMap = new Map<
    QuickSourceProgressName,
    StepRoutes
  >([
    ['nameSite', 'site-selection'],
    ['locationContact', 'location-contacts'],
    ['datePricingInstructions', 'date-pricing-instructions'],
    ['vendorSelection', 'add-vendors'],
    ['review', 'review']
  ]);

  // Used when navigating back and forth with buttons.
  currentRouteToInformationMap: {
    'site-selection': {
      title: string;
      nextRoute: 'location-contacts';
      previousRoute: null;
      requirements: {
        name: 'A bid name is required.';
        SiteId: 'A Site must be selected.';
      };
      quickSourceProgressName: 'nameSite';
    };
    'location-contacts': {
      title: string;
      nextRoute: 'date-pricing-instructions';
      previousRoute: 'site-selection';
      requirements: {
        'Address.address1': 'A work location address is required.';
        'Address.city': 'A work location city is required';
        'Address.state': 'A work location State/Province/Territory must be selected';
        'Address.zip': 'A work location postal code is required.';
        contactFirstName: 'A contact first name is required.';
        contactLastName: 'A contact last name is required.';
        contactPhone: 'A contact mobile phone is required.';
        contactEmail: 'A contact email is required.';
      };
      quickSourceProgressName: 'locationContact';
    };
    'date-pricing-instructions': {
      title: string;
      nextRoute: 'add-vendors';
      previousRoute: 'location-contacts';
      requirements: {
        startDate: 'A start date is required.';
        dueDate: 'A due date is required.';
        instructionFiles: 'Instructions or Files must be added.';
        startDateLessThanToday: "Start date cannot be less than today's date.";
        dueDateGreaterThanStartDate: 'Due date cannot be greater than the start date.';
        reminderDateInvalid: 'Reminder date must be less than start date and due date.';
        'ProposalRequestFiles.fileTooLarge': 'An added file is too large.';
        'ProposalRequestFiles.totalFileSize': 'Total file size is too large.';
      };
      quickSourceProgressName: 'datePricingInstructions';
    };
    'add-vendors': {
      title: string;
      nextRoute: 'review';
      previousRoute: 'date-pricing-instructions';
      // NEEDS TO BE UPDATED
      requirements: {};
      quickSourceProgressName: 'vendorSelection';
    };
    review: {
      title: string;
      previousRoute: 'add-vendors';
      nextRoute: null;
      // NEEDS TO BE UPDATED
      requirements: {};
      quickSourceProgressName: 'review';
    };
  } = {
    'site-selection': {
      title: '',
      nextRoute: 'location-contacts',
      previousRoute: null,
      requirements: {
        name: 'A bid name is required.',
        SiteId: 'A Site must be selected.'
      },
      quickSourceProgressName: 'nameSite'
    },
    'location-contacts': {
      title: '',
      nextRoute: 'date-pricing-instructions',
      previousRoute: 'site-selection',
      requirements: {
        'Address.address1': 'A work location address is required.',
        'Address.city': 'A work location city is required',
        'Address.state':
          'A work location State/Province/Territory must be selected',
        'Address.zip': 'A work location postal code is required.',
        contactFirstName: 'A contact first name is required.',
        contactLastName: 'A contact last name is required.',
        contactPhone: 'A contact mobile phone is required.',
        contactEmail: 'A contact email is required.'
      },
      quickSourceProgressName: 'locationContact'
    },
    'date-pricing-instructions': {
      title: '',
      nextRoute: 'add-vendors',
      previousRoute: 'location-contacts',
      requirements: {
        startDate: 'A start date is required.',
        dueDate: 'A due date is required.',
        instructionFiles: 'Instructions or Files must be added.',
        startDateLessThanToday: "Start date cannot be less than today's date.",
        dueDateGreaterThanStartDate:
          'Due date cannot be greater than the start date.',
        reminderDateInvalid:
          'Reminder date must be less than start date and due date.',
        'ProposalRequestFiles.fileTooLarge': 'An added file is too large.',
        'ProposalRequestFiles.totalFileSize': 'Total file size is too large.'
      },
      quickSourceProgressName: 'datePricingInstructions'
    },
    'add-vendors': {
      title: '',
      nextRoute: 'review',
      previousRoute: 'date-pricing-instructions',
      requirements: {
        'ProposalRequestVendors.needSelection': 'A Vendor must be added.'
      },
      quickSourceProgressName: 'vendorSelection'
    },
    review: {
      title: '',
      nextRoute: null,
      previousRoute: 'add-vendors',

      // NEEDS TO BE UPDATED
      requirements: {},
      quickSourceProgressName: 'review'
    }
  };

  action?: 'view';
  CompanyId!: number;
  ProposalRequestId?: number;
  currentRoute: StepRoutes = 'site-selection';
  ProposalRequest?: ProposalRequest;
  ProposalRequestSite?: Site;
  ProposalRequestFormGroup = new ProposalRequestForm();

  isSite(data: Site | ProposalRequestForm | null | undefined): data is Site {
    return data instanceof Site;
  }

  generateProposalRequestLineItemFormGroup() {
    return new FormGroup({
      id: new FormControl<number | undefined>(
        { value: undefined, disabled: false },
        { nonNullable: true }
      ),
      ProposalRequestId: new FormControl<number | undefined>(
        { value: undefined, disabled: false },
        { nonNullable: true }
      ),
      text: new FormControl<string>('', {
        nonNullable: true,
        validators: [Validators.required]
      })
    });
  }

  getError(
    errors: { [key: string]: any },
    requirementParts: string[]
  ): string | false {
    if (requirementParts.length === 0) {
      throw this.ToastService.addErrorToast(
        'Error',
        'There was an error retrieving form errors.',
        { returnError: true }
      );
    }

    const requirement = requirementParts.shift();

    if (!requirement) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing requirement when retrieving form errors.',
        { returnError: true }
      );
    }

    if (requirementParts.length === 0) {
      return errors[requirement] || false;
    }

    if (errors[requirement]) {
      return this.getError(errors[requirement], requirementParts);
    }

    return false;
  }

  goToStep(quickSourceProgressName: QuickSourceProgressName) {
    const route = this.QuickSourceProgressNameToRouteMap.get(
      quickSourceProgressName
    );

    if (!route) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing route information.',
        { returnError: true }
      );
    }

    const navArray = [
      'client',
      this.CompanyId,
      'proposal-request',
      this.ProposalRequestId
    ];
    if (this.action === 'view') {
      navArray.push(this.action);
    }
    navArray.push(route);
    this.router.navigate(navArray);
    this.currentRoute = route;
  }

  continue() {
    const errors = this.UtilityService.generateValidationErrorsObject(
      this.ProposalRequestFormGroup
    );

    const currentRouteInformation =
      this.currentRouteToInformationMap[this.currentRoute];
    const { requirements, nextRoute } = currentRouteInformation;

    if (errors) {
      for (let requirement of Object.keys(requirements)) {
        const requirementParts = requirement.split('.');
        const error = this.getError(errors, requirementParts);
        if (error) {
          const requirementError =
            requirements[requirement as keyof typeof requirements];
          throw this.ToastService.addErrorToast('Error', requirementError, {
            returnError: true
          });
        }
      }
    }

    const proposalRequestLineItemErrorsArr: { [key: string]: any }[] = [];
    const ProposalRequestLineItemsForms =
      this.ProposalRequestFormGroup.controls.ProposalRequestLineItems;

    if (ProposalRequestLineItemsForms) {
      ProposalRequestLineItemsForms.controls.forEach((formControl) => {
        const errors =
          this.UtilityService.generateValidationErrorsObject(formControl);

        if (errors) {
          proposalRequestLineItemErrorsArr.push(errors);
        }
      });
    }

    if (proposalRequestLineItemErrorsArr.length > 0) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Proposal request line items are missing names.',
        { returnError: true }
      );
    }

    if (!nextRoute) {
      throw this.ToastService.addErrorToast(
        'Error',
        'There was an error navigating to the next step.',
        { returnError: true }
      );
    }

    (() => {
      if (!this.action) {
        const { quickSourceProgressName: currentQuickSourceProgressName } =
          this.currentRouteToInformationMap[this.currentRoute];
        const { quickSourceProgressName: nextQuickSourceProgressName } =
          this.currentRouteToInformationMap[nextRoute];

        const CurrentQuickSourceProgress = this.QuickSourceProgressMap.get(
          currentQuickSourceProgressName
        );

        if (!CurrentQuickSourceProgress) {
          throw this.ToastService.addErrorToast(
            'Error',
            'Missing current QuickSource progress information.',
            { returnError: true }
          );
        }

        const NextQuickSourceProgress: QuickSourceProgress =
          this.QuickSourceProgressMap.get(nextQuickSourceProgressName) || {
            name: nextQuickSourceProgressName,
            status: 'Started',
            ProposalRequestId: this.ProposalRequestId
          };

        const QuickSourceProgresses = [
          CurrentQuickSourceProgress,
          NextQuickSourceProgress
        ];

        QuickSourceProgresses.forEach((QuickSourceProgress) => {
          const { id, name, status } = QuickSourceProgress;

          if (!id || status === 'Started') {
            if (name === currentQuickSourceProgressName) {
              Object.assign(QuickSourceProgress, { status: 'Complete' });
            }
            const QuickSourceProgressFormGroup = new QuickSourceProgressForm();
            QuickSourceProgressFormGroup.patchValue(QuickSourceProgress);
            this.ProposalRequestFormGroup.controls[
              'QuickSourceProgresses'
            ].push(QuickSourceProgressFormGroup);
          }
        });

        return this.ProposalRequestService.saveProposalRequest(
          this.ProposalRequestFormGroup.getRawValue()
        );
      }

      return of<ProposalRequest | null>(null);
    })().subscribe({
      next: (ProposalRequest) => {
        if (ProposalRequest) {
          const {
            id,
            ProposalRequestFiles,
            ProposalRequestLineItems,
            ProposalRequestVendors,
            QuickSourceProgresses
          } = ProposalRequest;

          this.ProposalRequestFormGroup.patchValue(ProposalRequest);

          if (QuickSourceProgresses) {
            QuickSourceProgresses.forEach((QuickSourceProgressRecord) => {
              const { name } = QuickSourceProgressRecord;

              if (!name) {
                throw this.ToastService.addErrorToast(
                  'Error',
                  'QuickSource progress is missing name.',
                  { returnError: true }
                );
              }

              this.QuickSourceProgressMap.set(
                name,
                new QuickSourceProgress(QuickSourceProgressRecord)
              );
            });

            this.ProposalRequestFormGroup.controls[
              'QuickSourceProgresses'
            ].clear();
          }

          this.ProposalRequestFormGroup.patchValue({ id });
          this.ProposalRequestId = id;

          if (ProposalRequestFiles) {
            const ProposalRequestFilesFormArray =
              this.ProposalRequestFormGroup.controls['ProposalRequestFiles'];
            ProposalRequestFilesFormArray.clear();
            ProposalRequestFiles.forEach((prf) => {
              const ProposalRequestFileFormGroup =
                new ProposalRequestFileForm();
              ProposalRequestFileFormGroup.patchValue(prf);
              ProposalRequestFilesFormArray.push(ProposalRequestFileFormGroup);
            });
          }

          if (ProposalRequestLineItems) {
            const ProposalRequestLineItemsFormArray =
              this.ProposalRequestFormGroup.controls[
                'ProposalRequestLineItems'
              ];
            ProposalRequestLineItemsFormArray.clear();
            ProposalRequestLineItems.forEach((prli) => {
              const ProposalRequestLineItemFormGroup =
                new ProposalRequestLineItemForm();
              ProposalRequestLineItemFormGroup.patchValue(prli);
              ProposalRequestLineItemsFormArray.push(
                ProposalRequestLineItemFormGroup
              );
            });
          }

          if (ProposalRequestVendors) {
            const ProposalRequestVendorsFormArray =
              this.ProposalRequestFormGroup.controls['ProposalRequestVendors'];
            if (ProposalRequestVendorsFormArray) {
              ProposalRequestVendorsFormArray.clear();
              ProposalRequestVendors.forEach((prv) => {
                const ProposalRequestVendorFormGroup =
                  new ProposalRequestVendorForm();
                ProposalRequestVendorFormGroup.patchValue(prv);
                ProposalRequestVendorsFormArray.push(
                  ProposalRequestVendorFormGroup
                );
              });
            }
          }
        }

        // Reset form errors when navigating to the next route.
        this.ProposalRequestFormGroup.markAsPristine();
        this.currentRoute = nextRoute;
        const navArray = [
          'client',
          this.CompanyId,
          'proposal-request',
          this.ProposalRequestId
        ];
        if (this.action === 'view') {
          navArray.push(this.action);
        }
        navArray.push(nextRoute);
        this.router.navigate(navArray);
      }
    });
  }

  previous() {
    const ProposalRequestVendorsFormArray =
      this.ProposalRequestFormGroup.controls['ProposalRequestVendors'];

    // If the User is moving back to a previous quicksource step, and no Vendors have been added to the Form,
    // remove the control, if the Control exists. Otherwise a validation error will be thrown.
    if (
      ProposalRequestVendorsFormArray &&
      ProposalRequestVendorsFormArray.length === 0
    ) {
      this.ProposalRequestFormGroup.removeControl('ProposalRequestVendors');
    }

    const currentRouteInformation =
      this.currentRouteToInformationMap[this.currentRoute];
    const { previousRoute } = currentRouteInformation;

    if (!previousRoute) {
      throw this.ToastService.addErrorToast(
        'Error',
        'There was an error navigating to the previous step.',
        { returnError: true }
      );
    }

    this.currentRoute = previousRoute;
    const navArray = [
      'client',
      this.CompanyId,
      'proposal-request',
      this.ProposalRequestId
    ];
    if (this.action === 'view') {
      navArray.push(this.action);
    }
    navArray.push(previousRoute);
    this.router.navigate(navArray);
  }

  finish() {
    // Need to save the current QuickSource progress as complete.
    const QuickSourceProgressRecord = this.QuickSourceProgressMap.get(
      'review'
    ) || {
      name: 'review',
      status: 'Complete',
      ProposalRequestId: this.ProposalRequestId
    };

    const { id, status } = QuickSourceProgressRecord;

    if ((!id || status === 'Started') && !this.action) {
      Object.assign(QuickSourceProgressRecord, { status: 'Complete' });
      const QuickSourceProgressFormGroup = new QuickSourceProgressForm();
      QuickSourceProgressFormGroup.patchValue(QuickSourceProgressRecord);
      this.ProposalRequestFormGroup.controls['QuickSourceProgresses'].push(
        QuickSourceProgressFormGroup
      );
    }

    const {
      value: { status: proposalRequestStatus }
    } = this.ProposalRequestFormGroup;

    this.ProposalRequestService.saveProposalRequest(
      Object.assign(this.ProposalRequestFormGroup.value, {
        status: 'RELEASED'
      }),
      { isReRelease: proposalRequestStatus === 'RELEASED' }
    ).subscribe({
      next: (ProposalRequest) => {
        const { QuickSourceProgresses } = ProposalRequest;

        if (QuickSourceProgresses) {
          QuickSourceProgresses.forEach((QuickSourceProgressRecord) => {
            const { name } = QuickSourceProgressRecord;

            if (!name) {
              throw this.ToastService.addErrorToast(
                'Error',
                'QuickSource progress is missing name.',
                { returnError: true }
              );
            }

            this.QuickSourceProgressMap.set(
              name,
              new QuickSourceProgress(QuickSourceProgressRecord)
            );
          });

          this.ProposalRequestFormGroup.controls[
            'QuickSourceProgresses'
          ].clear();
        }

        if (this.ProposalRequest?.status === 'RELEASED') {
          this.ToastService.addSuccessToast(
            'Bid Released Successfully',
            'Bid updates have been sent to all vendors.'
          );
        } else {
          this.ToastService.addSuccessToast(
            'Bid Released Successfully',
            'Bid has been sent to all vendors.'
          );
        }

        this.router.navigate(['client', this.CompanyId, 'proposal-requests']);
      }
    });
  }

  cancel() {
    this.router.navigate(['client', this.CompanyId, 'proposal-requests']);
  }

  downloadProposalRequestFile(ProposalRequestFile: ProposalRequestFile) {
    if (!this.ProposalRequest) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing Proposal Request.',
        { returnError: true }
      );
    }

    const { id: ProposalRequestId } = this.ProposalRequest;

    if (!ProposalRequestId) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Proposal Request does not have id.',
        { returnError: true }
      );
    }

    const { id: ProposalRequestFileId } = ProposalRequestFile;

    this.ProposalRequestService.getProposalRequest({
      id: ProposalRequestId,
      ProposalRequestFile: JSON.stringify({
        id: ProposalRequestFileId,
        File: {}
      })
    }).subscribe({
      next: (ProposalRequest) => {
        if (!ProposalRequest) {
          throw this.ToastService.addErrorToast(
            'Error',
            'Missing bid information.',
            { returnError: true }
          );
        }

        const { id: ProposalRequestId, ProposalRequestFiles } = ProposalRequest;

        if (!ProposalRequestId) {
          throw this.ToastService.addErrorToast(
            'Error',
            'Missing ProposalRequestId.',
            { returnError: true }
          );
        }

        if (
          !Array.isArray(ProposalRequestFiles) ||
          ProposalRequestFiles.length !== 1
        ) {
          throw this.ToastService.addErrorToast(
            'Error',
            'Missing Proposal File information.',
            { returnError: true }
          );
        }

        const { File } = ProposalRequestFiles[0];

        if (!File) {
          throw this.ToastService.addErrorToast(
            'Error',
            'Missing File information.',
            { returnError: true }
          );
        }

        const { id: FileId, FileContent, filename, filetype, filepath } = File;

        if (!FileId) {
          throw this.ToastService.addErrorToast('Error', 'Missing FileId.', {
            returnError: true
          });
        }

        if (filepath) {
          this.ProposalRequestService.getProposalRequestFileS3PresignedUrl(
            ProposalRequestId,
            FileId
          ).subscribe({
            next: ({ presignedUrl, filename }) => {
              saveAs(presignedUrl, filename);
            }
          });
        } else {
          if (!filename) {
            throw this.ToastService.addErrorToast(
              'Error',
              'Missing File name.',
              {
                returnError: true
              }
            );
          }

          if (!filetype) {
            throw this.ToastService.addErrorToast(
              'Error',
              'Missing File type.',
              {
                returnError: true
              }
            );
          }

          if (!FileContent) {
            throw this.ToastService.addErrorToast(
              'Error',
              'Missing File data record.',
              { returnError: true }
            );
          }

          const { filecontents } = FileContent;

          if (!filecontents) {
            throw this.ToastService.addErrorToast(
              'Error',
              'Missing file data.',
              {
                returnError: true
              }
            );
          }

          saveAs(`data:${filetype};base64,${filecontents}`, filename);
        }
      }
    });
  }

  ngOnInit(): void {
    this.currentRoute = this.router.url.split('/').pop() as StepRoutes;

    this.route.paramMap.subscribe({
      next: (paramMap) => {
        const ProposalRequestId = Number(paramMap.get('ProposalRequestId'));
        const CompanyId = Number(paramMap.get('CompanyId'));
        const action = paramMap.get('action');

        if (action) {
          if (action !== 'view') {
            throw this.ToastService.addErrorToast(
              'Error',
              'Invalid url action.',
              { returnError: true }
            );
          }

          this.action = action;
          this.disableInput = true;
        }

        if (!CompanyId) {
          throw this.ToastService.addErrorToast('Error', 'Missing Client Id.', {
            returnError: true
          });
        }

        this.CompanyId = CompanyId;

        this.ProposalRequestFormGroup.patchValue({ CompanyId: this.CompanyId });

        this.CompanyService.getCompany(this.CompanyId, {
          attributes: JSON.stringify(['id', 'country'])
        }).subscribe({
          next: (Client) => {
            if (!Client) {
              throw this.ToastService.addErrorToast(
                'Error',
                'Missing Client information.',
                { returnError: true }
              );
            }

            this.Company = new Company(Client);
            this._cdr.markForCheck();
          }
        });

        (() => {
          if (ProposalRequestId) {
            this.ProposalRequestId = ProposalRequestId;

            return this.ProposalRequestService.getProposalRequest({
              id: this.ProposalRequestId,
              SubNationalDivision: JSON.stringify({}),
              Site: JSON.stringify({
                StreetAddress: {}
              }),
              ProposalRequestLineItem: JSON.stringify({}),
              ProposalRequestFile: JSON.stringify({
                File: {}
              }),
              Address: JSON.stringify({}),
              ProposalRequestVendor: JSON.stringify({
                Address: {}
              }),
              QuickSourceProgress: JSON.stringify({})
            });
          } else {
            if (this.disableInput) {
              this.UtilityService.recursivelyDisableFormControls(
                this.ProposalRequestFormGroup
              );
            }

            return of<ProposalRequest | null>(null);
          }
        })().subscribe({
          next: (ProposalRequest) => {
            if (ProposalRequest) {
              this.ProposalRequestFormGroup.patchValue(ProposalRequest);

              const {
                ProposalRequestLineItems,
                ProposalRequestFiles,
                ProposalRequestVendors,
                Site: ProposalRequestSite,
                QuickSourceProgresses
              } = ProposalRequest;

              if (QuickSourceProgresses) {
                QuickSourceProgresses.forEach((QuickSourceProgressRecord) => {
                  const { name } = QuickSourceProgressRecord;

                  if (!name) {
                    throw this.ToastService.addErrorToast(
                      'Error',
                      'QuickSource progress is missing name.',
                      { returnError: true }
                    );
                  }

                  this.QuickSourceProgressMap.set(
                    name,
                    new QuickSourceProgress(QuickSourceProgressRecord)
                  );
                });
              }

              if (ProposalRequestSite) {
                this.ProposalRequestSite = new Site(ProposalRequestSite);
              }

              if (Array.isArray(ProposalRequestLineItems)) {
                const ProposalRequestLineItemsFormArray =
                  this.ProposalRequestFormGroup.controls[
                    'ProposalRequestLineItems'
                  ];
                ProposalRequestLineItems.forEach((prli) => {
                  const ProposalRequestLineItemFormGroup =
                    this.generateProposalRequestLineItemFormGroup();
                  ProposalRequestLineItemFormGroup.patchValue(prli);
                  ProposalRequestLineItemsFormArray.push(
                    ProposalRequestLineItemFormGroup
                  );
                });
              }

              if (Array.isArray(ProposalRequestFiles)) {
                const ProposalRequestFilesArray =
                  this.ProposalRequestFormGroup.controls[
                    'ProposalRequestFiles'
                  ];
                ProposalRequestFiles.forEach((prf) => {
                  const ProposalRequestFileFormGroup =
                    new ProposalRequestFileForm();
                  ProposalRequestFileFormGroup.patchValue(prf);
                  ProposalRequestFilesArray.push(ProposalRequestFileFormGroup);
                });
              }

              if (
                Array.isArray(ProposalRequestVendors) &&
                ProposalRequestVendors.length > 0
              ) {
                if (
                  !this.ProposalRequestFormGroup.controls[
                    'ProposalRequestVendors'
                  ]
                ) {
                  this.ProposalRequestFormGroup.setControl(
                    'ProposalRequestVendors',
                    new FormArray<ProposalRequestVendorForm>([])
                  );
                }
                const ProposalRequestVendorsFormArray =
                  this.ProposalRequestFormGroup.controls[
                    'ProposalRequestVendors'
                  ];
                ProposalRequestVendors.forEach((prv) => {
                  const ProposalRequestVendorFormGroup =
                    new ProposalRequestVendorForm();
                  ProposalRequestVendorFormGroup.patchValue(prv);
                  ProposalRequestVendorsFormArray!.push(
                    ProposalRequestVendorFormGroup
                  );
                });
              }

              if (this.disableInput) {
                this.UtilityService.recursivelyDisableFormControls(
                  this.ProposalRequestFormGroup
                );
              }

              this.ProposalRequest = ProposalRequest;
            }
            this._cdr.markForCheck();

            const routesAndTitles: {
              route:
                | 'site-selection'
                | 'location-contacts'
                | 'date-pricing-instructions'
                | 'add-vendors'
                | 'review';
              title: string;
            }[] = [
              {
                route: 'site-selection',
                title:
                  this.ProposalRequest?.status === 'RELEASED' &&
                  action !== 'view'
                    ? 'Edit Site and Name Bid'
                    : 'Choose Site and Name Bid'
              },
              {
                route: 'location-contacts',
                title:
                  this.ProposalRequest?.status === 'RELEASED' &&
                  action !== 'view'
                    ? 'Edit Location and Contact Information'
                    : 'Location and Contact Information'
              },
              {
                route: 'date-pricing-instructions',
                title:
                  this.ProposalRequest?.status === 'RELEASED' &&
                  action !== 'view'
                    ? 'Edit Dates, Pricing, and Instructions'
                    : 'Dates, Pricing and Instructions'
              },
              {
                route: 'add-vendors',
                title:
                  this.ProposalRequest?.status === 'RELEASED' &&
                  action !== 'view'
                    ? 'Edit Vendors'
                    : 'Add Vendors to Bid'
              },
              { route: 'review', title: 'Review Bid' }
            ];

            routesAndTitles.forEach((routeTitleObject) => {
              const { route, title } = routeTitleObject;
              Object.assign(this.currentRouteToInformationMap[route], {
                title
              });
            });

            // QuickSourceProgressMap
            const quickSourceProgressName =
              this.currentRouteToInformationMap[this.currentRoute]
                .quickSourceProgressName;

            // Need to save the started progress. However, can only save the started progress
            // if the Proposal Request has been saved before.
            // Finish backend validation.

            if (
              !this.QuickSourceProgressMap.get(quickSourceProgressName) &&
              !this.action
            ) {
              this.QuickSourceProgressMap.set(
                quickSourceProgressName,
                new QuickSourceProgress({
                  name: quickSourceProgressName,
                  status: 'Started',
                  ProposalRequestId: this.ProposalRequestId
                })
              );
            }
          }
        });
      }
    });
  }
}
